import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Eye, Download } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import axiosInstance from "../../api/axiosInstance";
import { useApp } from "../../context/formflow/AppContext";
import { ROLE_PALETTE } from "../../constants/formflow/fieldTypes";
import { getFileUrl } from "../../utils/formflow/helpers";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

const num = (v) => parseFloat(v) || 0;

function getRoleColor(roles, roleId) {
  const idx = roles.findIndex((r) => r.id === roleId);
  return ROLE_PALETTE[Math.max(0, idx) % ROLE_PALETTE.length];
}

function isFilled(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "boolean") return true;
  return Boolean(value);
}

function formatValue(field, value) {
  if (field.type === "checkbox") return value === true || value === "true" ? "✓" : "";
  if (field.type === "date" && value) return new Date(value).toLocaleDateString();
  if ((field.type === "signature" || field.type === "initials") && typeof value === "string" && value.startsWith("data:")) return value;
  return value;
}

function ViewFieldOverlay({ field, value }) {
  const displayValue = formatValue(field, value);
  if (!displayValue) return null;

  return (
    <div
      className="absolute overflow-hidden pointer-events-none"
      style={{
        left: `${num(field.x)}%`,
        top: `${num(field.y)}%`,
        width: `${num(field.w)}%`,
        height: `${num(field.h)}%`,
        zIndex: 5,
      }}
    >
      {typeof displayValue === "string" && displayValue.startsWith("data:") ? (
        <img src={displayValue} alt={field.label} className="w-full h-full object-contain" />
      ) : (
        <div
          className="w-full h-full flex items-center overflow-hidden px-1 text-gray-900 font-medium whitespace-nowrap"
          style={{
            fontSize: `${Math.max(9, field.h * 4)}px`,
            fontFamily: field.type === "signature" || field.type === "initials" ? "cursive" : "inherit",
          }}
        >
          <span className="truncate">{displayValue}</span>
        </div>
      )}
    </div>
  );
}

export function CompletedFormViewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { showToast } = useApp();
  const [loading, setLoading] = useState(true);
  const [completedData, setCompletedData] = useState(null);
  const [pages, setPages] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const pdfScrollRef = useRef(null);

  useEffect(() => {
    const fetchCompletedForm = async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get(`/api/formflow/completed/${token}`);
        if (!data.success) {
          showToast("Completed form not available", "error");
          return navigate("/formflow");
        }

        setCompletedData(data);
        if (data.envelope?.file?.public_url) {
          await loadPdfPages(getFileUrl(data.envelope.file.public_url));
        }
      } catch (err) {
        console.error(err);
        showToast(err.response?.data?.message || "Failed to load completed form", "error");
        navigate("/formflow");
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedForm();
  }, [token]);

  const loadPdfPages = async (url) => {
    try {
      const pdf = await pdfjsLib.getDocument(url).promise;
      const imgs = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const vp = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width;
        canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
        imgs.push(canvas.toDataURL());
      }
      setPages(imgs);
    } catch (err) {
      console.error("PDF render error", err);
      showToast("Failed to render PDF", "error");
    }
  };

  const { envelope, form, file, roles, signers, overlaysByPage, valuesByField } = useMemo(() => {
    if (!completedData?.envelope) return {};

    const { envelope, form, file, roles, signers } = completedData.envelope;
    const valuesByField = {};

    (signers || []).forEach((signer) => {
      (signer.fieldValues || []).forEach((fv) => {
        valuesByField[fv.fieldId] = fv.value;
      });
    });

    const overlaysByPage = {};
    (roles || []).forEach((role) => {
      const roleColor = getRoleColor(roles, role.id);
      (role.fields || []).forEach((field) => {
        const value = valuesByField[field.id];
        if (!value) return;
        if (!overlaysByPage[field.pageIndex]) overlaysByPage[field.pageIndex] = [];
        overlaysByPage[field.pageIndex].push({ field, value, roleColor });
      });
    });

    return { envelope, form, file, roles, signers, overlaysByPage, valuesByField };
  }, [completedData]);


  const generateFilledPdf = async () => {
    try {
      if (!file?.public_url) return;

      const existingPdfBytes = await fetch(getFileUrl(file.public_url)).then((r) => r.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const allFields = (roles || []).flatMap((role) => role.fields || []);

      for (const field of allFields) {
        const val = valuesByField?.[field.id];
        if (!isFilled(val)) continue;

        const page = pdfDoc.getPage(field.pageIndex);
        const { width, height } = page.getSize();
        const x = (field.x / 100) * width;
        const y = height - (field.y / 100) * height;
        const fieldWidth = (field.w / 100) * width;
        const fieldHeight = (field.h / 100) * height;
        const fontSize = Math.max(8, fieldHeight * 0.6);

        if (field.type === "checkbox") {
          if (val === true || val === "true") {
            page.drawText("✓", {
              x,
              y: y - fieldHeight,
              size: fontSize,
              color: rgb(0, 0.5, 0),
              font,
            });
          }
          continue;
        }

        if ((field.type === "signature" || field.type === "initials") && typeof val === "string" && val.startsWith("data:")) {
          try {
            const pngImage = await pdfDoc.embedPng(val);
            page.drawImage(pngImage, { x, y: y - fieldHeight, width: fieldWidth, height: fieldHeight });
          } catch {
            try {
              const jpgImage = await pdfDoc.embedJpg(val);
              page.drawImage(jpgImage, { x, y: y - fieldHeight, width: fieldWidth, height: fieldHeight });
            } catch (e) {
              console.error("Signature image failed", e);
            }
          }
          continue;
        }

        const text = field.type === "date" ? new Date(val).toLocaleDateString() : String(val);
        page.drawText(text, {
          x: x + 2,
          y: y - fieldHeight + fieldHeight * 0.2,
          size: fontSize,
          color: rgb(0, 0, 0),
          font,
          maxWidth: fieldWidth,
        });
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const docName = (form?.name || envelope?.title || "completed_contract").replace(/\s+/g, "_");
      a.download = `${docName}_completed.pdf`;
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);
      showToast("PDF downloaded successfully", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to generate PDF", "error");
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    await generateFilledPdf();
    setDownloading(false);
  };

  if (loading) return <div className="min-h-screen bg-slate-50 p-8 text-center">Loading completed form...</div>;
  if (!completedData?.envelope) return null;

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="flex flex-col max-w-7xl mx-auto">
        <div className="bg-white border-b px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <div className="text-xs text-gray-400">Completed Form</div>
            <h2 className="text-lg font-bold">{form?.name || envelope?.title}</h2>
          </div>
          <div className="text-xs text-gray-500 text-right">
            <div>Viewing as {completedData.viewer?.name}</div>
            <div>{completedData.viewer?.email}</div>
          </div>
        </div>

        <div className="flex gap-6 p-6">
          <div ref={pdfScrollRef} className="flex-1 bg-white shadow-lg overflow-y-auto rounded-lg max-h-[88vh]">
            {pages.length > 0 ? (
              pages.map((src, pageIndex) => (
                <div key={pageIndex} className="relative mb-5">
                  <img src={src} alt={`Page ${pageIndex + 1}`} className="w-full block" />
                  {(overlaysByPage?.[pageIndex] || []).map((overlay, idx) => (
                    <ViewFieldOverlay key={`${overlay.field.id}-${idx}`} field={overlay.field} value={overlay.value} />
                  ))}
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">Loading PDF preview...</div>
            )}
          </div>

          <div className="w-80 shrink-0 flex flex-col gap-4">
            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-bold text-sm mb-2">Signing Status</h3>
              <p className="text-xs text-gray-600 mb-1">Envelope #{envelope.id}</p>
              <p className="text-xs text-green-700 flex items-center gap-1"><Check size={12} /> All signing users completed</p>
              {envelope.downloaded_at && <p className="text-xs text-gray-400 mt-1">Opened {new Date(envelope.downloaded_at).toLocaleString()}</p>}
              <button onClick={handleDownloadPdf} disabled={downloading} className="mt-4 w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 shadow transition-all bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                {downloading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Download PDF
                  </>
                )}
              </button>
            </div>

            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Eye size={14} /> Signers</h3>
              <div className="space-y-2">
                {(signers || []).map((signer) => (
                  <div key={signer.id} className="text-xs border border-gray-100 rounded-lg p-2">
                    <div className="font-semibold text-gray-700">{signer.name}</div>
                    <div className="text-gray-400">{signer.email}</div>
                    {signer.hidden_fields_viewed_at && <div className="text-[10px] text-green-600 mt-1">Hidden fields viewed</div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-bold text-sm mb-3">Field Values</h3>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {(roles || []).flatMap((role) => (role.fields || []).map((field) => ({ role, field }))).map(({ role, field }) => {
                  const value = valuesByField?.[field.id];
                  if (!value) return null;
                  const displayValue = formatValue(field, value);
                  return (
                    <div key={field.id} className="border-b border-gray-100 pb-1">
                      <div className="text-xs font-semibold text-gray-600">{field.label}</div>
                      <div className="text-[10px] text-gray-400">{role.name}</div>
                      {typeof displayValue === "string" && displayValue.startsWith("data:") ? (
                        <img src={displayValue} alt={field.label} className="max-h-12 mt-1" />
                      ) : (
                        <div className="text-xs text-gray-800 wrap-break-word">{displayValue}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
